import { get } from 'svelte/store';

import { activityId, isLogin, userID } from '../store/index';

import { resolveBallPageId } from './ballPageId';
import { getCookieConfig } from './passport';

import { getHost } from '@/utils/cluster';
import { getCookie, setCookie } from '@/utils/cookie';

const ACTIVITY_CTX_COOKIE = 'mx_ball_cc_activity_ctx';

export type RewardClaimPayload = {
    pageId: string;
    uid: string;
    activityId: string;
};

type ActivityCtxCookie = RewardClaimPayload & {
    ticket: string;
};

const setActivityCtxCookie = (value: string) => {
    setCookie(ACTIVITY_CTX_COOKIE, value, {
        expires: value ? 1 : -1,
        domain: getHost(),
        path: '/'
    });
};

const resolveUid = () => getCookie('uid') || get(userID);

/** 清除 activityId 绑定（登出时与 uid/ticket 一并失效） */
export const clearActivityIdBinding = () => {
    setActivityCtxCookie('');
};

/**
 * 按 pageId + uid + ticket 写入 cookie，供回访/预加载复用
 * （与领奖落盘同一绑定，不单独使用 localStorage）
 */
export function saveRewardClaim(payload: RewardClaimPayload): void {
    const { pageId, uid, activityId: aid } = payload;
    const ticket = getCookieConfig().ticket;

    if (!pageId || !uid || !aid || !ticket) return;

    setActivityCtxCookie(
        JSON.stringify({
            pageId,
            uid,
            ticket,
            activityId: String(aid)
        } satisfies ActivityCtxCookie)
    );
}

/** cookie 中 uid、ticket、pageId 均匹配时返回 activityId */
export function getMatchedRewardActivityId(): string | null {
    const pageId = resolveBallPageId();
    const { uid, ticket } = getCookieConfig();
    const resolvedUid = uid || resolveUid();

    if (!pageId || !resolvedUid || !ticket) return null;

    const raw = getCookie(ACTIVITY_CTX_COOKIE);

    if (!raw) return null;

    try {
        const data = JSON.parse(raw) as Partial<ActivityCtxCookie>;

        const aid =
            data.activityId === undefined || data.activityId === null
                ? ''
                : String(data.activityId);

        if (
            data.pageId === pageId &&
            data.uid === resolvedUid &&
            data.ticket === ticket &&
            aid
        ) {
            return aid;
        }

        return null;
    } catch {
        return null;
    }
}

/** 已登录且 cookie 绑定命中时，同步 activityId 到 store */
export function applyRewardClaimToStore(): void {
    if (!get(isLogin)) return;

    const aid = getMatchedRewardActivityId();

    if (!aid) return;

    activityId.set(aid);
}

/** config / 领奖后统一写入 cookie 绑定 */
export const persistActivityIdForSession = saveRewardClaim;
